import {
  Banner,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import type { QuickView } from 'lib-analytics-types';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import { useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { analyticsApi } from 'apis/analytics';
import {
  HookFormCheckbox,
  HookFormText,
  HookFormTextarea,
} from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './quickViewModal.module.scss';
import { AnalyticsQuickViewModalType } from './quickViewModal.types';

const formId = 'analytics-quick-view-form';

type FieldValues = Pick<QuickView, 'name' | 'description' | 'shared'>;

/**
 * A modal what for creating, updating, and duplicating quick views in
 * Analytics.
 */
export const AnalyticsQuickViewModal = createAppModal(
  { type: 'analytics:quickView' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const hasAnalyticsAccess = useHasAccess('analytics', {
      fallbackRole: 'teamlead',
    });

    const notify = useNotifications();
    const navigate = useAppNavigate();
    const analyticsState = useAppSelector((state) => state.analytics);
    const { mode = 'create', dashboardId, quickView, location } = modal.arg;

    // The dashboard object corresponding to the dashboardId.
    const dashboard = analyticsApi.endpoints.readDashboards.useQueryState(
      undefined,
      {
        selectFromResult: ({ data }) =>
          data?.data.dashboards.find(({ id }) => id === dashboardId) ??
          analyticsState.dashboard!,
      },
    );

    const [createQuickView, createState] =
      analyticsApi.useCreateQuickViewMutation();
    const [updateQuickView, updateState] =
      analyticsApi.useUpdateQuickViewMutation();

    const form = useForm<FieldValues>({
      mode: 'all',
      defaultValues: {
        name: quickView
          ? mode === 'duplicate'
            ? `${quickView.name} (copy)`
            : quickView.name
          : '',
        description: quickView?.description ?? '',
        shared: quickView?.shared ?? false,
      },
    });

    /** Extracts the quick view params from the state. */
    const getParamsFromState = useCallback(
      (dashboardId: string): QuickView['params'] => ({
        ...pick(analyticsState, ['period', 'filters', 'settings']),
        context: analyticsState.context[dashboardId],
      }),
      [analyticsState],
    );

    /** Handles creating a new quick view. */
    const handleCreateQuickView = async (values: FieldValues) => {
      if (!dashboardId) return;

      try {
        const params: QuickView['params'] = getParamsFromState(dashboardId);

        const {
          data: { quick_view: createdQuickView },
        } = await createQuickView({
          dashboardId,
          quickView: { ...values, params },
        }).unwrap();

        ampli.analyticsQuickViewCreated({
          dashboard: dashboard.title,
          filters: params.filters?.map((filter) => filter.field) ?? [],
          period:
            params.period.values.length === 1
              ? params.period.values[0].value
              : 'dateWithDefaultTime',
          shared: values.shared,
        });

        ampli.analyticsQuickViewSharingToggled({
          dashboard: dashboard.title,
          shared: values.shared,
        });

        notify(
          {
            title: 'Success',
            text: sanitizeHtml(
              `"${createdQuickView.name}" has been added to the overview page.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 5000,
            leftAction: {
              text: 'See overview page',
              action: () => {
                navigate({ path: '/analytics/quick-views' });
              },
            },
          },
          NotificationType.Success,
        );

        navigate({
          path: '/analytics/dashboard/:slug/quick-view/:id',
          params: {
            path: {
              slug: dashboard.slug,
              id: createdQuickView.id,
            },
          },
        });

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while creating the quick view.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles updating an existing quick view. */
    const handleUpdateQuickView = async (values: FieldValues) => {
      if (!quickView) return;

      try {
        const params: QuickView['params'] = {
          ...quickView.params,
          ...getParamsFromState(quickView.dashboard_id),
        };

        const {
          data: { quick_view: updatedQuickView },
        } = await updateQuickView({
          id: quickView.id,
          dashboardId: quickView.dashboard_id,
          quickView: { ...values, params },
        }).unwrap();

        ampli.analyticsQuickViewUpdated({
          dashboard: dashboard.title,
          filters: params.filters?.map((filter) => filter.field) ?? [],
          period:
            params.period.values.length === 1
              ? params.period.values[0].value
              : 'dateWithDefaultTime',
          shared: values.shared,
        });

        // Track only if the sharing status has changed.
        if (quickView.shared !== values.shared) {
          ampli.analyticsQuickViewSharingToggled({
            dashboard: dashboard.title,
            shared: values.shared,
          });
        }

        meta?.resolve({ quickView: updatedQuickView });

        notify(
          {
            title: 'Success',
            text: sanitizeHtml(
              `Changes to "${values.name}" have been saved.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while updating the quick view.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles creating a new quick view based on an existing one. */
    const handleDuplicateQuickView = async (values: FieldValues) => {
      if (!quickView) return;

      try {
        const { params } = quickView;

        const {
          data: { quick_view: createdQuickView },
        } = await createQuickView({
          dashboardId: quickView.dashboard_id,
          quickView: { ...values, params },
        }).unwrap();

        ampli.analyticsQuickViewDuplicated({
          dashboard: dashboard.title,
          filters: params.filters?.map((filter) => filter.field) ?? [],
          period:
            params.period.values.length === 1
              ? params.period.values[0].value
              : 'dateWithDefaultTime',
          shared: values.shared,
        });

        ampli.analyticsQuickViewSharingToggled({
          dashboard: dashboard.title,
          shared: values.shared,
        });

        notify(
          {
            title: 'Success',
            text: sanitizeHtml(
              `"${quickView.name}" has been copied as "${createdQuickView.name}".`,
              'plainText',
            ),
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        navigate({
          path: '/analytics/dashboard/:slug/quick-view/:id',
          params: {
            path: {
              slug: dashboard.slug,
              id: createdQuickView.id,
            },
          },
        });

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while copying the quick view.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles valid form submit. */
    const handleValidSubmit: SubmitHandler<FieldValues> = (values) => {
      if (mode === 'create') {
        handleCreateQuickView(values);
      } else if (mode === 'update') {
        handleUpdateQuickView(values);
      } else if (mode === 'duplicate') {
        handleDuplicateQuickView(values);
      }
    };

    /** Various texts according to the mode of the modal. */
    const texts: Record<
      AnalyticsQuickViewModalType,
      Record<'title' | 'button', string>
    > = {
      create: {
        title: 'Create quick view',
        button: 'Create quick view',
      },
      duplicate: {
        title: 'Duplicate quick view',
        button: 'Duplicate quick view',
      },
      update: {
        title: 'Edit quick view',
        button: 'Save changes',
      },
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title={texts[mode].title} onClose={hide} />

        <ModalBody>
          <form
            id={formId}
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(handleValidSubmit)}
          >
            <FormGrid>
              <FormField>
                <HookFormText
                  name="name"
                  label="Name"
                  control={form.control}
                  rules={{
                    required: 'Name is required',
                  }}
                  componentProps={{
                    autoFocus: true,
                  }}
                />
              </FormField>

              <FormField>
                <HookFormTextarea
                  name="description"
                  label="Description"
                  control={form.control}
                  componentProps={{
                    height: 102,
                  }}
                />
              </FormField>

              {hasAnalyticsAccess && (
                <FormField>
                  <HookFormCheckbox
                    name="shared"
                    label="Make this quick view public"
                    control={form.control}
                    helpIcon={{
                      dark: true,
                      tooltipClassName: styles.helpTooltip,
                      text: (
                        <Text.Paragraph variant="2" color="white">
                          Select the checkbox to make the view available from
                          the <b>Quick views page</b> for all users with Team
                          Lead or Admin roles. You can always change this again
                          later.
                        </Text.Paragraph>
                      ),
                    }}
                  />
                </FormField>
              )}

              {mode === 'update' && location !== 'quickViews' && (
                <FormField>
                  <Banner
                    variant="info"
                    description={
                      <Text.Paragraph variant="4">
                        Any changes made to the <b>period and filters</b> will
                        be saved.
                      </Text.Paragraph>
                    }
                  />
                </FormField>
              )}
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName={texts[mode].button}
          confirmOnClick={noop}
          confirmProps={{
            type: 'submit',
            isLoading: createState.isLoading || updateState.isLoading,
            disabled: !form.formState.isValid || form.formState.isSubmitting,
            form: formId,
          }}
        />
      </Modal>
    );
  },
);
